/**
 * Преобразования Мёбиуса, сохраняющие модель Пуанкаре (диск).
 *
 * Все изометрии хранятся как матрицы группы SU(1,1): `[[α, β], [β̄, ᾱ]]`
 * с условием `|α|² − |β|² = 1`, действующие на точку диска как
 * `z ↦ (α·z + β) / (β̄·z + ᾱ)`. Композиция = произведение матриц, что даёт
 * устойчивое накопление преобразований и простую инверсию.
 *
 * Требования: 4.1
 */

import type { Complex, Mobius } from './map-types.js';
import {
  ONE,
  ZERO,
  add,
  conjugate,
  divide,
  fromPolar,
  modulusSquared,
  multiply,
  negate,
  scale,
} from './complex.js';

/** Тождественное преобразование (единичная матрица SU(1,1)). */
export function identity(): Mobius {
  return { alpha: { ...ONE }, beta: { ...ZERO } };
}

/**
 * Определитель матрицы SU(1,1): `|α|² − |β|²`.
 * Для корректной изометрии диска должен равняться `1`.
 */
export function determinant(m: Mobius): number {
  return modulusSquared(m.alpha) - modulusSquared(m.beta);
}

/**
 * Нормализовать матрицу так, чтобы `|α|² − |β|² = 1`.
 * Компенсирует накопление численной ошибки при длинных композициях.
 * Бросает ошибку, если определитель неположителен (не изометрия диска).
 */
export function normalize(m: Mobius): Mobius {
  const det = determinant(m);
  if (det <= 0) {
    throw new Error(`Mobius matrix is not a disk isometry (det = ${det})`);
  }
  const s = 1 / Math.sqrt(det);
  return { alpha: scale(m.alpha, s), beta: scale(m.beta, s) };
}

/**
 * Применить преобразование к точке диска: `z ↦ (α·z + β) / (β̄·z + ᾱ)`.
 */
export function apply(m: Mobius, z: Complex): Complex {
  const numerator = add(multiply(m.alpha, z), m.beta);
  const denominator = add(multiply(conjugate(m.beta), z), conjugate(m.alpha));
  return divide(numerator, denominator);
}

/**
 * Композиция преобразований: результат применяется как `first(second(z))`.
 * Соответствует произведению матриц `M(first) · M(second)`.
 */
export function compose(first: Mobius, second: Mobius): Mobius {
  // [[α1,β1],[β̄1,ᾱ1]] · [[α2,β2],[β̄2,ᾱ2]]
  // верхняя строка результата:
  //   α = α1·α2 + β1·β̄2
  //   β = α1·β2 + β1·ᾱ2
  const alpha = add(
    multiply(first.alpha, second.alpha),
    multiply(first.beta, conjugate(second.beta)),
  );
  const beta = add(
    multiply(first.alpha, second.beta),
    multiply(first.beta, conjugate(second.alpha)),
  );
  return { alpha, beta };
}

/**
 * Композиция цепочки преобразований слева направо: `chain(a, b, c)` действует
 * как `a(b(c(z)))`. Без аргументов возвращает тождество.
 */
export function chain(...transforms: Mobius[]): Mobius {
  return transforms.reduce((acc, m) => compose(acc, m), identity());
}

/**
 * Инверсия преобразования. Для SU(1,1) с `det = 1`:
 * обратная к `[[α, β], [β̄, ᾱ]]` равна `[[ᾱ, −β], [−β̄, α]]`.
 */
export function invert(m: Mobius): Mobius {
  const det = determinant(m);
  if (det === 0) {
    throw new Error('Cannot invert singular Mobius transform');
  }
  // Для нормированной матрицы (det = 1) деление на det опускается,
  // но выполняем его для устойчивости к ненормированным матрицам.
  const alpha = scale(conjugate(m.alpha), 1 / det);
  const beta = scale(negate(m.beta), 1 / det);
  return { alpha, beta };
}

/**
 * Поворот вокруг центра диска на угол `theta` (радианы): `z ↦ e^{iθ}·z`.
 * Матрица SU(1,1): `α = e^{iθ/2}`, `β = 0`.
 */
export function rotation(theta: number): Mobius {
  return { alpha: fromPolar(1, theta / 2), beta: { ...ZERO } };
}

/**
 * Изометрия, переводящая точку `a` (|a| < 1) в центр диска: `z ↦ (z − a)/(1 − ā·z)`.
 * Матрица SU(1,1) с нормировкой `1/√(1 − |a|²)`.
 */
export function translateToOrigin(a: Complex): Mobius {
  const denom = 1 - modulusSquared(a);
  if (denom <= 0) {
    throw new Error('translateToOrigin requires a point strictly inside the disk (|a| < 1)');
  }
  const s = 1 / Math.sqrt(denom);
  return { alpha: { re: s, im: 0 }, beta: scale(negate(a), s) };
}

/**
 * Изометрия, переводящая центр диска в точку `a` (|a| < 1).
 * Обратная к {@link translateToOrigin}.
 */
export function translateFromOrigin(a: Complex): Mobius {
  return invert(translateToOrigin(a));
}

/**
 * Гиперболический сдвиг (перенос) вдоль геодезической, проходящей через центр
 * в направлении `phi`, на гиперболическое расстояние `t`.
 * Центр диска переходит в точку `tanh(t/2)·e^{iφ}`.
 * Матрица SU(1,1): `α = cosh(t/2)`, `β = sinh(t/2)·e^{iφ}`.
 * @param t гиперболическое расстояние сдвига
 * @param phi направление оси сдвига в радианах (по умолчанию вдоль вещественной оси)
 */
export function hyperbolicTranslation(t: number, phi = 0): Mobius {
  const half = t / 2;
  return {
    alpha: { re: Math.cosh(half), im: 0 },
    beta: fromPolar(Math.sinh(half), phi),
  };
}

/**
 * Приблизительное равенство двух преобразований по элементам матрицы.
 * Учитывает знаковую неоднозначность SU(1,1) (`M` и `−M` задают одно
 * преобразование), сравнивая как с `n`, так и с `−n`.
 * @param tolerance абсолютный допуск (по умолчанию `1e-9`)
 */
export function mobiusEquals(m: Mobius, n: Mobius, tolerance = 1e-9): boolean {
  const close = (a: Complex, b: Complex): boolean =>
    Math.hypot(a.re - b.re, a.im - b.im) <= tolerance;
  const sameSign = close(m.alpha, n.alpha) && close(m.beta, n.beta);
  const oppSign =
    close(m.alpha, negate(n.alpha)) && close(m.beta, negate(n.beta));
  return sameSign || oppSign;
}
